/* eslint-disable max-lines-per-function */
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { SpinnerMask } from '@atlassian/amkt-common';
import { getReviewById, voteReview } from '@atlassian/mpac-api-client';
import { log } from '@atlassian/mpac-utils';
import React, { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { VOTE } from '../../utils';
import { DislikeFilledIcon, DislikeIcon, LikeFilledIcon, LikeOutlineIcon, ReviewActionsContainer, ReviewActionsOption, SpinnerWrapper } from './VoteReview.styles';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// eslint-disable-next-line complexity
export const VoteReview = _ref => {
  let {
    mpacBaseUrl,
    totalVotes,
    helpfulVotes,
    voteUpEndpoint,
    voteDownEndpoint,
    isLoggedIn,
    loginHref,
    reviewId,
    appKey,
    isOwnReview = false
  } = _ref;
  const [vote, setVote] = useState(null);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [helpfulVotesState, setHelpfulVotesState] = useState(helpfulVotes);
  const [totalVotesState, setTotalVotesState] = useState(totalVotes);
  const [showVoteBanner, setShowVoteBanner] = useState(null);

  // partner is viewing their own app and not allowed to vote on their own reviews
  const isPartnerApp = isLoggedIn && voteUpEndpoint === undefined && voteDownEndpoint === undefined;
  const reviewOptionRef = {};
  const {
    isPending: isVotingInProgress,
    isFulfilled: isVoteSuccessful,
    isRejected: isVoteRejected,
    error: voteReviewError,
    run
  } = useAsync({
    deferFn: voteReview
  });
  const showVoteUpSpinner = isVotingInProgress && vote === VOTE.YES;
  const showVoteDownSpinner = isVotingInProgress && vote === VOTE.NO;
  const setReviewOptionRef = option => el => {
    reviewOptionRef[option] = el;
  };
  useEffect(() => {
    if (isVoteRejected) {
      setShowVoteBanner('failure');
      log.error('ReviewsTab', 'failed to vote a review', {
        error: voteReviewError
      });
    } else {
      setShowVoteBanner(null);
    }
  }, [isVoteRejected, voteReviewError]);
  useEffect(() => {
    if (isVoteSuccessful) {
      setShowVoteBanner('success');
      const getReviewDataById = async () => {
        const reviewData = await getReviewById(appKey, reviewId);
        if (reviewData) {
          setHelpfulVotesState(reviewData.helpfulVotes);
          setTotalVotesState(reviewData.totalVotes);
        }
      };
      getReviewDataById();
    }
  }, [appKey, isVoteSuccessful, reviewId]);
  const handleVoteReview = (e, voteOption, endpoint, voteState) => {
    e.preventDefault();
    if (isLoggedIn && endpoint) {
      const voteReviewUrl = "".concat(mpacBaseUrl).concat(endpoint);
      setVote(voteOption);
      run(voteReviewUrl, voteState);
      if (voteOption === VOTE.YES) {
        setLiked(voteState);
        setDisliked(false);
      } else if (voteOption === VOTE.NO) {
        setDisliked(voteState);
        setLiked(false);
      }
    } else if (loginHref) {
      window.location.href = loginHref;
    }
  };
  const handleKeyDown = (event, voteOption, endpoint, voteState) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleVoteReview(event, voteOption, endpoint, voteState);
    }
    if (voteOption === VOTE.YES && (event.key === 'ArrowDown' || event.key === 'ArrowRight')) {
      var _reviewOptionRef$VOTE;
      (_reviewOptionRef$VOTE = reviewOptionRef[VOTE.NO]) === null || _reviewOptionRef$VOTE === void 0 ? void 0 : _reviewOptionRef$VOTE.focus();
    }
    if (voteOption === VOTE.NO && (event.key === 'ArrowUp' || event.key === 'ArrowLeft')) {
      var _reviewOptionRef$VOTE2;
      (_reviewOptionRef$VOTE2 = reviewOptionRef[VOTE.YES]) === null || _reviewOptionRef$VOTE2 === void 0 ? void 0 : _reviewOptionRef$VOTE2.focus();
    }
  };
  const resetVoteBanner = () => setShowVoteBanner(null);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FlagGroup, {
      children: showVoteBanner === 'failure' && /*#__PURE__*/_jsx(AutoDismissFlag, {
        id: "vote-feedback-not-submitted",
        icon: /*#__PURE__*/_jsx(CrossCircleIcon, {
          label: "Failure",
          primaryColor: "var(--ds-icon-danger, #C9372C)"
        }),
        title: "Feedback not submitted",
        description: "We\u2019re are unable to load your response. Refresh the page or try again in some time.",
        onDismissed: resetVoteBanner,
        actions: [{
          content: 'Got it',
          onClick: resetVoteBanner
        }]
      }, 1)
    }), isOwnReview || isPartnerApp ? totalVotes > 0 && /*#__PURE__*/_jsxs(ReviewActionsContainer, {
      children: [/*#__PURE__*/_jsxs("span", {
        children: [helpfulVotesState, " out of ", totalVotesState, " found this review helpful"]
      }), "\u2022"]
    }) : /*#__PURE__*/_jsxs(ReviewActionsContainer, {
      role: "radiogroup",
      "aria-labelledby": "review-action-legend",
      children: [/*#__PURE__*/_jsx("span", {
        id: "review-action-legend",
        children: "Was this review helpful?"
      }), /*#__PURE__*/_jsx(VisuallyHidden, {
        "aria-live": "polite",
        role: "status",
        "aria-relevant": "all",
        children: isVoteSuccessful && "your response ".concat(vote, " was ").concat(liked || disliked ? 'saved' : 'removed')
      }), /*#__PURE__*/_jsxs(ReviewActionsOption, {
        "aria-label": "yes",
        role: "radio",
        tabIndex: vote !== VOTE.NO ? 0 : -1,
        "aria-checked": vote === VOTE.YES,
        onClick: e => handleVoteReview(e, VOTE.YES, voteUpEndpoint, !(liked && isVoteSuccessful)),
        onKeyDown: e => handleKeyDown(e, VOTE.YES, voteUpEndpoint, !(liked && isVoteSuccessful)),
        ref: setReviewOptionRef(VOTE.YES),
        children: [showVoteUpSpinner ? /*#__PURE__*/_jsx(SpinnerWrapper, {
          children: /*#__PURE__*/_jsx(SpinnerMask, {
            active: true,
            delay: 200,
            size: 16,
            minHeight: 16
          })
        }) : liked && isVoteSuccessful ? /*#__PURE__*/_jsx(LikeFilledIcon, {
          label: ""
        }) : /*#__PURE__*/_jsx(LikeOutlineIcon, {
          label: ""
        }), /*#__PURE__*/_jsxs("span", {
          id: "likeCount",
          "aria-label": "".concat(helpfulVotesState, " people found it useful."),
          children: ["(", helpfulVotesState, ")"]
        })]
      }), /*#__PURE__*/_jsxs(ReviewActionsOption, {
        "aria-label": "no",
        role: "radio",
        tabIndex: vote === VOTE.NO ? 0 : -1,
        "aria-checked": vote === VOTE.NO,
        onClick: e => handleVoteReview(e, VOTE.NO, voteDownEndpoint, !(disliked && isVoteSuccessful)),
        onKeyDown: e => handleKeyDown(e, VOTE.NO, voteDownEndpoint, !(disliked && isVoteSuccessful)),
        ref: setReviewOptionRef(VOTE.NO),
        children: [showVoteDownSpinner ? /*#__PURE__*/_jsx(SpinnerWrapper, {
          children: /*#__PURE__*/_jsx(SpinnerMask, {
            active: true,
            delay: 200,
            size: 16,
            minHeight: 16
          })
        }) : disliked && isVoteSuccessful ? /*#__PURE__*/_jsx(DislikeFilledIcon, {
          label: ""
        }) : /*#__PURE__*/_jsx(DislikeIcon, {
          label: ""
        }), /*#__PURE__*/_jsxs("span", {
          id: "disLikeCount",
          "aria-label": "".concat(totalVotesState - helpfulVotesState, " people didn't find it useful"),
          children: ["(", totalVotesState - helpfulVotesState, ")"]
        })]
      }), "\u2022"]
    })]
  });
};